import { keyBy } from 'lodash';
import { createAction } from 'redux-actions';
import {
  SELL,
  SELL_TRANSACTION,
  TransactionTypeNameMap,
  TransactionTypes,
} from 'src/settings/settings';
import { callApi, getEndpoint } from 'src/utils/api';
import {
  getAPITransactionType,
  isAutoExchange,
  isAutomaticSell,
  isLanding,
  isRecurringBuy,
  transactionType,
  isExchange,
  isRothConversion,
} from 'src/utils/transactions-util';
import { UserFundsActionTypes as act } from './actionTypes';

const AGREEMENT_MAP = 'AGREEMENT_MAP';

/**
 * Optimizes a single agreement map. See optimizeAgreementMapOfMaps below.
 * @param {AgreementMap} agreementMap
 * @returns {AgreementMapEx}
 */
function optimizeAgreementMap(agreementMap) {
  Object.keys(agreementMap).forEach((arrayKey) => {
    const lookupKey = arrayKey.replace(/Agreements$/, 'Lookup');
    if (lookupKey === arrayKey) {
      // got a key we didn't expect, ignore
      return;
    }
    agreementMap[lookupKey] = keyBy(agreementMap[arrayKey]);
  });
}

/**
 * Given an agreement map with array lists of agreements, make a corresponding field for
 * each array that is a lookup map for those agreements.
 *
 * @param {Object.<string, AgreementMap>} mapOfMaps Map of lists of agreements for each attribute
 * @returns {Object.<string, AgreementMapEx>} Map of lists of agreements for each attribute with lookups
 */
function optimizeAgreementMapOfMaps(mapOfMaps) {
  if (typeof mapOfMaps !== 'object' || mapOfMaps === null) {
    return {};
  }
  Object.keys(mapOfMaps).forEach((key) => optimizeAgreementMap(mapOfMaps[key]));
  return mapOfMaps;
}

function createMapByAgreementID(userFunds) {
  if (typeof userFunds !== 'object' || userFunds === null) {
    return {};
  }

  const agreementMap = {};
  // object should have retirement and non-retirement groupings
  if (Object.keys(userFunds)) {
    Object.keys(userFunds).forEach((grouping) => {
      if (grouping.indexOf('Agreements') !== -1) {
        // each grouping has agreement types defined underneath (TRADITIONAL IRA, JOINT-TENANT etc)
        Object.keys(userFunds[grouping]).forEach((agreementTypeKey) => {
          if (userFunds[grouping][agreementTypeKey]) {
            userFunds[grouping][agreementTypeKey].forEach((fund) => {
              if (fund?.agreementID) {
                agreementMap[fund.agreementID] = fund;
              }
            });
          }
        });
      }
    });
  }
  return agreementMap;
}

export default function userFunds(
  state = {
    isLoading: false,
    isLoaded: false,
    hasFailed: false,
    agreementMapIsLoaded: false,
    agreementMapHasFailed: false,
    agreementMapIsLoading: false,
    error: null,
    agreementMap: {},
    funds: [{}],
  },
  { payload, type }
) {
  switch (type) {
    case act.USER_FUNDS_PENDING:
      return {
        ...state,
        isLoaded: false,
        isLoading: true,
      };
    case act.USER_FUNDS_FULFILLED:
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        funds: payload.body ? [payload.body] : [{}],
        fundsByAgreementID: createMapByAgreementID(payload.body),
      };

    case act.USER_FUNDS_REJECTED:
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        hasFailed: true,
        funds: [{}],
        error: payload,
      };
    case `${AGREEMENT_MAP}_PENDING`:
      return {
        ...state,
        agreementMapIsLoading: true,
        agreementMapIsLoaded: false,
        agreementMapHasFailed: false,
        status: 'FETCHING',
      };
    case `${AGREEMENT_MAP}_FULFILLED`:
      return {
        ...state,
        agreementMap: optimizeAgreementMapOfMaps(payload.body),
        status: 'MAP RETRIEVED',
        agreementMapIsLoading: false,
        agreementMapIsLoaded: true,
        agreementMapHasFailed: false,
      };
    case `${AGREEMENT_MAP}_REJECTED`:
      return {
        ...state,
        agreementMap: state.agreementMap || {},
        status: 'FAILED',
        error: payload,
        agreementMapIsLoading: false,
        agreementMapIsLoaded: false,
        agreementMapHasFailed: true,
      };
    default:
      return state;
  }
}

const currentTransactionType =
  transactionType() === SELL_TRANSACTION
    ? TransactionTypes[SELL]
    : TransactionTypeNameMap[transactionType()];

export const getUserFunds = (
  transactionType = currentTransactionType,
  productTypeCategory = 'MutualFund'
) => {
  const has529AndMF =
    (isLanding() ||
      isRecurringBuy() ||
      transactionType === TransactionTypes.EXCHANGE) &&
    productTypeCategory === 'MutualFund';
  const productType =
    has529AndMF && !isRothConversion()
      ? ''
      : `productType=${productTypeCategory}&`;
  const transactionTypeParam = isAutomaticSell()
    ? 'AutomaticWithdrawal'
    : isAutoExchange()
    ? 'AutomaticExchange'
    : isRecurringBuy() || isLanding()
    ? 'AutomaticInvestment'
    : getAPITransactionType();

  return () => {
    const userFundEndpoint = getEndpoint('userFunds');
    return createAction(act.USER_FUNDS)(
      callApi(
        `${userFundEndpoint}${
          isExchange()
            ? `?transactionType=${transactionTypeParam}`
            : `?${productType}transactionType=${transactionTypeParam}`
        }`
      )
    );
  };
};

export const getBankMaps = () => {
  return () => {
    return createAction(AGREEMENT_MAP)(
      callApi(
        `${getEndpoint(
          'agreementMap'
        )}?transactionType=${getAPITransactionType()}`
      )
    );
  };
};
